import React from 'react';
import PropTypes from 'prop-types';
import { themeColors, components } from '@theme/classNames';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useLogger } from '@providers/loggerProvider';

// Error types - Keep runtime/compilation for local errors
const ERROR_TYPES = {
    COMPILATION: 'compilation',
    CHUNK: 'chunk',
    RUNTIME: 'runtime'
};

const CHUNK_RELOAD_STORAGE_PREFIX = 'brickbuddy:chunk-reload:';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false, // Tracks local rendering/compilation errors
            error: null,
            errorType: null,
        };
        this.clearLocalError = this.clearLocalError.bind(this);
    }

    static getDerivedStateFromError(error) {
        // Determine error type based on error properties and message for local errors
        let errorType = ERROR_TYPES.RUNTIME;

        if (error.message?.includes('Module not found') ||
            error.message?.includes('Compiled with problems')) {
            errorType = ERROR_TYPES.COMPILATION;
        }
        if (error.name === 'ChunkLoadError') {
            errorType = ERROR_TYPES.CHUNK;
        }
        // Removed connection/API/async checks - rely on global state for these

        // Log here minimally, main logging in componentDidCatch
        // console.error("Derived state from error:", error);

        return {
            hasError: true,
            error,
            errorType
        };
    }

    componentDidCatch(error, errorInfo) {
        // Use injected logger
        this.props.logger.error('Error caught by boundary:', { error: error.toString(), message: error.message, stack: error.stack, componentStack: errorInfo?.componentStack }, 'ErrorBoundary');
        // A deployment can remove a chunk referenced by an already-open tab.
        // Remember the asset across reloads so a persistent failure cannot loop.
        if (error.name === 'ChunkLoadError' && typeof error.request === 'string' && navigator.onLine) {
            const reloadKey = CHUNK_RELOAD_STORAGE_PREFIX + error.request;
            if (window.sessionStorage.getItem(reloadKey) === null) {
                window.sessionStorage.setItem(reloadKey, '1');
                window.location.reload();
            }
        }
    }

    // Method to clear local component error state
    clearLocalError() {
        this.props.logger.info('Dismissing local component error.', 'ErrorBoundary');
        this.setState({ hasError: false, error: null, errorType: null });
    }

    // Add this method
    getComponentErrorMessage() {
        const { error } = this.state;
        if (!error) return 'An unknown error occurred.';
        if (this.state.errorType === ERROR_TYPES.CHUNK) {
            return 'BrickBuddy could not load this page. Reload to get the latest version.';
        }
        // Return a concise message
        return error.message || 'An unexpected error happened in the component.';
    }

    // Add this method
    getComponentErrorDetails() {
        const { error } = this.state;
        if (this.state.errorType === ERROR_TYPES.CHUNK) return null;
        // Return the stack trace if available
        return error?.stack || null;
    }

    render() {
        const { children } = this.props;
        const { hasError, errorType } = this.state; // Local error state

        // --- Local Component Error Fallback ---
        // Render fallback UI IF a local component error occurred (local state)
        if (hasError) {
            const isCompilationError = errorType === ERROR_TYPES.COMPILATION;
            const errorTitle = errorType === ERROR_TYPES.CHUNK
                ? 'Page could not load'
                : isCompilationError ? 'Build Error' : 'Application Error';

            return (
                <div className={`p-4 my-4 rounded-lg ${themeColors.surface.DEFAULT} ${themeColors.text.primary} border ${themeColors.status.error.border}`} role="alert">
                    <div className="flex items-center gap-3 mb-3">
                        <ExclamationTriangleIcon className={`w-6 h-6 ${themeColors.status.error.text}`} aria-hidden="true" />
                        <h3 className={`text-lg font-semibold ${themeColors.status.error.text}`}>
                            {errorTitle}
                        </h3>
                    </div>
                    <p className={`mb-3 ${themeColors.text.secondary} text-sm`}>
                        {this.getComponentErrorMessage()}
                    </p>
                    {/* Component Error Details */}
                    {this.getComponentErrorDetails() && (
                        <pre className={`p-3 mb-4 rounded ${themeColors.background.dark} ${themeColors.text.secondary} text-xs font-mono overflow-auto max-h-60`}>
                            {this.getComponentErrorDetails()}
                        </pre>
                    )}
                    <div className="mt-4 flex flex-col sm:flex-row gap-3">
                        {/* Reload is often the best fix for runtime errors */}
                        <button
                            type="button"
                            onClick={() => window.location.reload()}
                            className={`${components.button.base} ${components.button.variants.primary} w-full sm:w-auto`}
                        >
                            Reload Page
                        </button>
                        {/* Allow dismissing local error - use with caution */}
                        <button
                            type="button"
                            onClick={this.clearLocalError}
                            className={`${components.button.base} ${components.button.variants.secondary} w-full sm:w-auto`}
                        >
                            Dismiss Error
                        </button>
                    </div>
                </div>
            );
        }

        // Render children if no errors (neither global API nor local component)
        return children;
    }
}

ErrorBoundary.propTypes = {
    children: PropTypes.node.isRequired,
    logger: PropTypes.shape({ // Logger is still required
        debug: PropTypes.func.isRequired,
        info: PropTypes.func.isRequired,
        warn: PropTypes.func.isRequired,
        error: PropTypes.func.isRequired,
    }).isRequired,
};

// Simple HOC to inject just the logger
const withLogger = (WrappedComponent) => {
    const ComponentWithLogger = (props) => {
        const logger = useLogger();

        if (!logger) {
            console.error("Logger not available in withLogger HOC for ErrorBoundary");
            // Fallback: render children without the ErrorBoundary wrapper
            return props.children || null;
        }

        return <WrappedComponent {...props} logger={logger} />;
    };

    // Set display name for DevTools
    const wrappedComponentName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
    ComponentWithLogger.displayName = `withLogger(${wrappedComponentName})`;

    return ComponentWithLogger;
};

// Export the ErrorBoundary component wrapped with the logger HOC
export default withLogger(ErrorBoundary);
